/**
 * sources.js — where a frame comes from, for the preview and for the export.
 *
 * Two very different jobs behind one call shape, `getFrame(clipIndex, srcTime)`:
 *
 *   PreviewSources — <video> elements playing in real time against a master
 *     clock. Never seek-per-frame: a seek costs 10–30 ms and doing two of them
 *     sixty times a second turns a preview into a slideshow. The elements play
 *     themselves and are only corrected when they drift.
 *
 *   ExportSources — exact frames at exact times. Prefers pulling decoded frames
 *     out of a WebCodecs decoder in temporal order (no seeking at all, hardware
 *     decode, GOP-independent) and falls back to seeking a <video> when the
 *     file is not mp4-family or the decoder setup fails.
 *
 * Preview AUDIO is deliberately not here. It is the finished mix played as one
 * buffer (see ui.js), because the mix is the only place ducking, per-clip
 * offsets and the music bed exist — previewing element audio instead would
 * play something the exported file does not contain.
 *
 * Exposes window.CloneSources.
 */
(function () {
'use strict';
if (window.CloneSources) return;

function loadMeta(file) {
    return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var v = document.createElement('video');
        v.preload = 'metadata';
        v.muted = true;
        v.playsInline = true;
        var done = false;
        var to = setTimeout(function () {
            if (done) return;
            done = true;
            URL.revokeObjectURL(url);
            reject(new Error('metadata-timeout'));
        }, 25000);

        v.onloadedmetadata = function () {
            if (done) return;
            done = true;
            clearTimeout(to);
            var d = v.duration;
            if (!isFinite(d) || d <= 0) {
                // Some WebM/MKV recordings report Infinity until they are seeked
                // to the end — a documented Chrome behaviour, not a broken file.
                v.currentTime = 1e6;
                v.ontimeupdate = function () {
                    v.ontimeupdate = null;
                    resolve({ url: url, el: v, duration: v.duration, width: v.videoWidth, height: v.videoHeight });
                    v.currentTime = 0;
                };
                return;
            }
            resolve({ url: url, el: v, duration: d, width: v.videoWidth, height: v.videoHeight });
        };
        v.onerror = function () {
            if (done) return;
            done = true;
            clearTimeout(to);
            URL.revokeObjectURL(url);
            reject(new Error('decode-failed'));
        };
        v.src = url;
    });
}

/* ───────────────────────────── preview ───────────────────────────── */

function PreviewSources(project) {
    this.p = project;
    // Keyed by CLIP ID, never by position. Undo can reorder or resurrect takes,
    // and a position-keyed store then hands take 2's frames to take 3 — a
    // composite that is confidently wrong with nothing thrown. Same reason the
    // matte store is id-keyed.
    this.els = {};
    this.playing = false;
}

PreviewSources.prototype.attach = function (clipId, el) {
    el.muted = true;
    el.playsInline = true;
    el.preload = 'auto';
    this.els[clipId] = el;
};

/** Whether this take still has a live element — undo needs to know. */
PreviewSources.prototype.has = function (clipId) { return !!this.els[clipId]; };

PreviewSources.prototype.elFor = function (i) {
    var c = this.p.clips[i];
    return c ? this.els[c.id] : null;
};

/**
 * Force ONE decoded frame into the element and resolve when it is there.
 *
 * ⚠️ `readyState === HAVE_ENOUGH_DATA` does NOT mean a frame can be drawn.
 * Measured on a freshly loaded element: readyState 4, videoWidth 640, no error
 * — and `drawImage` painted pure black. One seek later the same element painted
 * correctly. So the state flag reports that DATA has arrived, not that a frame
 * has been decoded into the compositor, and trusting it left the whole preview
 * black while the export (which decodes through WebCodecs) was perfect.
 *
 * The second half of the trap: seeking to 0 when the element is already at 0 is
 * a no-op — no `seeked`, no frame. So a preview parked at t=0, which is where
 * every project starts, could never un-blacken itself. The nudge to a non-zero
 * epsilon first is what breaks that.
 *
 * `requestVideoFrameCallback` is the precise signal ("a frame is now available
 * for presentation") and is used when the browser has it; `seeked` plus a frame
 * of slack is the fallback.
 */
PreviewSources.prototype.prime = function (clipId, atSec) {
    var el = this.els[clipId];
    if (!el) return Promise.resolve(false);
    var base = Math.max(0, atSec || 0);
    var self = this;

    function frameAt(t) {
        return new Promise(function (resolve) {
            var done = false;
            var fin = function () {
                if (done) return;
                done = true;
                el.removeEventListener('seeked', onSeek);
                resolve();
            };
            function onSeek() {
                if (el.requestVideoFrameCallback) return;   // rVFC will finish it
                requestAnimationFrame(function () { fin(); });
            }
            el.addEventListener('seeked', onSeek);
            if (el.requestVideoFrameCallback) {
                try { el.requestVideoFrameCallback(function () { fin(); }); } catch (_) {}
            }
            try { el.currentTime = t; } catch (_) { fin(); }
            setTimeout(fin, 8000);
        });
    }

    return new Promise(function (resolve) {
        var wait = el.readyState >= 1 ? Promise.resolve() : new Promise(function (r) {
            var f = function () { el.removeEventListener('loadedmetadata', f); r(); };
            el.addEventListener('loadedmetadata', f);
            try { el.load(); } catch (_) {}
            setTimeout(f, 12000);
        });

        wait.then(function () {
            var eps = base + 0.05;
            var dur = el.duration;
            if (isFinite(dur) && dur > 0.2 && eps > dur - 0.05) eps = Math.max(0, base - 0.05);
            return frameAt(eps).then(function () {
                return Math.abs(eps - base) > 0.001 ? frameAt(base) : null;
            });
        }).then(function () {
            resolve(!!(el && el.readyState >= 2));
        }).catch(function () { resolve(false); });
    });
};

PreviewSources.prototype.ready = function (clipId) {
    var el = this.els[clipId];
    return !!(el && el.readyState >= 2);
};

PreviewSources.prototype.detach = function (clipId) {
    var el = this.els[clipId];
    if (el) {
        // Out of the DOM, not just out of the map. A source element is attached
        // to the document so the browser will decode it; dropping the reference
        // leaves it there holding a decoder and a (now revoked) blob for the
        // life of the page, and swapping takes a few times on a 4K project
        // stacks those up.
        try { el.pause(); } catch (_) {}
        try { el.removeAttribute('src'); el.load(); } catch (_) {}
        try { el.remove(); } catch (_) {}
    }
    delete this.els[clipId];
};

PreviewSources.prototype.getFrame = function (i) {
    var el = this.elFor(i);
    if (!el || el.readyState < 2) return null;
    return el;
};

/**
 * Bring every element in line with composition time `t`.
 *
 * Returns a promise that resolves once every element it MOVED has actually
 * presented the new frame. That await is not optional: setting `currentTime` is
 * asynchronous, and rendering on the next line draws while the element is
 * mid-seek — which the renderer turns into a solid background-coloured frame,
 * i.e. a black flash on every scrub.
 *
 * `hard` forces the seek (scrubbing, a jump, the start of playback); otherwise
 * elements are left alone unless they have drifted more than a couple of frames,
 * because correcting a playing element every frame is what makes a preview
 * stutter.
 */
PreviewSources.prototype.sync = function (t, playing, hard) {
    var CS = window.CloneScene;
    var waits = [];

    for (var i = 0; i < this.p.clips.length; i++) {
        var c = this.p.clips[i], el = this.els[c && c.id];
        if (!c || !el) continue;
        var st = CS.sourceTimeAt(c, t);
        if (st == null) { if (!el.paused) el.pause(); continue; }

        var live = CS.isLive(c, t);
        var want = Math.max(0, Math.min(st, (c.duration || st) - 0.02));

        if (hard || Math.abs(el.currentTime - want) > 0.14) {
            waits.push(seekAndPresent(el, want));
        }
        if (playing && live) {
            if (el.paused) { var pr = el.play(); if (pr && pr.catch) pr.catch(function () {}); }
        } else if (!el.paused) {
            el.pause();
        }
    }
    return waits.length ? Promise.all(waits) : Promise.resolve();
};

function seekAndPresent(el, t) {
    if (Math.abs(el.currentTime - t) < 0.004 && el.readyState >= 2) return Promise.resolve();
    return new Promise(function (resolve) {
        var done = false;
        function fin() {
            if (done) return;
            done = true;
            el.removeEventListener('seeked', onSeek);
            resolve();
        }
        function onSeek() {
            if (el.requestVideoFrameCallback) return;
            requestAnimationFrame(fin);
        }
        el.addEventListener('seeked', onSeek);
        if (el.requestVideoFrameCallback) {
            try { el.requestVideoFrameCallback(function () { fin(); }); } catch (_) {}
        }
        try { el.currentTime = t; } catch (_) { fin(); }
        setTimeout(fin, 4000);
    });
}

/** True when at least one clip in the CURRENT list can be drawn right now. */
PreviewSources.prototype.anyReady = function () {
    for (var i = 0; i < this.p.clips.length; i++) {
        var el = this.elFor(i);
        if (el && el.readyState >= 2) return true;
    }
    return false;
};

PreviewSources.prototype.pauseAll = function () {
    for (var id in this.els) {
        var el = this.els[id];
        if (el && !el.paused) { try { el.pause(); } catch (_) {} }
    }
};

/* ───────────────────────────── export ─────────────────────────────
   Built per export and disposed with it. `mode` reports which path won so the
   telemetry can tell a slow export from a fallback one. */

async function makeExportSources(project, signal) {
    var VM = window.VideoMergeWebCodecs;
    var clips = project.clips;
    var allMp4 = !!(VM && VM.isMp4Family && clips.every(function (c) { return c && VM.isMp4Family(c.file); }));

    if (allMp4 && VM && VM.demux && VM.DecodeStream && VM.ClipCursor) {
        try {
            var src = await decoderPull(project, VM, signal);
            return src;
        } catch (e) {
            if (e && e.name === 'AbortError') throw e;
            try {
                window.__ae && window.__ae('feature_use', {
                    slug: 'clone-effect', f: 'decoder_pull_fallback',
                    err: ((e && e.message) || '').slice(0, 60)
                });
            } catch (_) {}
        }
    }
    return await seekPull(project);
}

async function decoderPull(project, VM, signal) {
    await VM.loadMp4Box();
    var CS = window.CloneScene;
    var cursors = [];
    try {
        for (var i = 0; i < project.clips.length; i++) {
            if (signal && signal.aborted) throw new DOMException('Aborted', 'AbortError');
            var c = project.clips[i];
            var d = await VM.demux(c.file);
            if (!d || !d.samples || !d.samples.length) throw new Error('no-samples');
            if (/avc1|h264/i.test(d.codec || '') && !d.desc) throw new Error('avcc-missing');

            var durs = d.samples.slice(0, Math.min(90, d.samples.length))
                .map(function (s) { return s.dur; })
                .filter(function (x) { return x > 0; })
                .sort(function (a, b) { return a - b; });
            var sfps = durs.length ? (1e6 / durs[durs.length >> 1]) : 30;

            // The cursor is opened over the clip's own trimmed range. `hold`
            // freezes at the ends, so nothing outside it is ever requested.
            var startUs = Math.round(c.trimIn * 1e6);
            var endUs = Math.round((c.trimIn + CS.clipDuration(c)) * 1e6);
            var stream = new VM.DecodeStream(d, startUs, endUs);
            cursors.push(new VM.ClipCursor(stream, sfps));
        }
    } catch (e) {
        cursors.forEach(function (cu) { try { cu.close(); } catch (_) {} });
        throw e;
    }

    var frames = [];
    return {
        mode: 'decoder',
        prepare: async function (t) {
            var jobs = [];
            for (var i = 0; i < project.clips.length; i++) {
                (function (i) {
                    var c = project.clips[i];
                    var st = CS.sourceTimeAt(c, t);
                    if (st == null) { frames[i] = null; return; }
                    jobs.push(cursors[i].frameAt(Math.round(st * 1e6)).then(function (f) { frames[i] = f; }));
                })(i);
            }
            await Promise.all(jobs);
        },
        getFrame: function (i) { return frames[i] || null; },
        close: async function () {
            for (var i = 0; i < cursors.length; i++) {
                try { await cursors[i].close(); } catch (_) {}
            }
        }
    };
}

async function seekPull(project) {
    var CS = window.CloneScene;
    var els = [];
    for (var i = 0; i < project.clips.length; i++) {
        var c = project.clips[i];
        var v = document.createElement('video');
        v.muted = true; v.playsInline = true; v.preload = 'auto';
        v.src = c.url;
        await new Promise(function (res, rej) {
            var ok = false;
            v.onloadeddata = function () { if (!ok) { ok = true; res(); } };
            v.onerror = function () { if (!ok) { ok = true; rej(new Error('export-src-load')); } };
            setTimeout(function () { if (!ok) { ok = true; res(); } }, 15000);
        });
        els.push(v);
    }

    function seekTo(v, t) {
        return new Promise(function (resolve) {
            if (Math.abs(v.currentTime - t) < 0.003) { resolve(); return; }
            var done = false;
            var fin = function () { if (done) return; done = true; v.onseeked = null; resolve(); };
            v.onseeked = fin;
            // A seek that never lands must not stall the whole export — the
            // previous frame is a far better outcome than a hang.
            setTimeout(fin, 3000);
            try { v.currentTime = t; } catch (_) { fin(); }
        });
    }

    return {
        mode: 'seek',
        prepare: async function (t) {
            var jobs = [];
            for (var i = 0; i < project.clips.length; i++) {
                var c = project.clips[i];
                var st = CS.sourceTimeAt(c, t);
                if (st == null) continue;
                var want = Math.max(0, Math.min(st, (c.duration || st) - 0.02));
                jobs.push(seekTo(els[i], want));
            }
            await Promise.all(jobs);
        },
        getFrame: function (i) {
            var c = project.clips[i];
            var el = els[i];
            if (!el || el.readyState < 2) return null;
            return el;
        },
        close: async function () {
            els.forEach(function (v) {
                try { v.pause(); v.removeAttribute('src'); v.load(); } catch (_) {}
            });
        }
    };
}

window.CloneSources = {
    loadMeta: loadMeta,
    PreviewSources: PreviewSources,
    makeExportSources: makeExportSources
};
})();
